<template>
  <multiselect
    ref="select"
    v-bind="$attrs"
    :modelValue="completeValue"
    :options="options"
    :track-by="trackBy"
    :taggable="taggable"
    @update:modelValue="onChange"
    @tag="addTag"
    @focus="$emit('focus', $event)"
    @blur="$emit('blur', $event)"
    :class="['key-multiselect', 'multiselect--clearable', `multiselect-${size}`]"
  >
    <template #beforeList>
      <template v-if="showSelectAll || showSelectNone || computedShowLoadAndSelectAll">
        <li class="select-all-or-none">
          <b-button variant="link" size="sm" style="padding:0" v-if="showSelectAll" @click="selectAll">Select All</b-button>
          <b-button variant="link" size="sm" style="padding:0" v-if="computedShowLoadAndSelectAll" @click="loadAndSelectAll">Load & Select All</b-button>
          <b-button variant="link" size="sm" style="padding:0" v-if="showSelectNone" @click="selectNone">Select None</b-button>
        </li>
      </template>
      <slot name="beforeList" />
    </template>

    <template #afterList>
      <template v-if="showAddNewItem">
        <hr class="after-list-divider" />
        <li class="add-new-item multiselect__element">
          <span class="multiselect__option" @click.stop="addNewItem">
            &lt; Add a new {{ resourceName }} &gt;
          </span>
        </li>
      </template>
      <slot name="afterList" />
    </template>

    <!-- TODO: Do we need to iterate a copy of $slots without beforeList, afterList, and clear? -->
    <template v-for="(_, name) in passThruSlots" #[name]="slotData">
      <slot :name="name" v-bind="slotData" />
    </template>

    <!-- magnifying glass implementation when multiple=false using single label -->
    <template #singleLabel="{ option }">
      <font-awesome-icon
        v-if="showMagnifyingGlass"
        icon="magnifying-glass"
        class="tw:text-2xs tw:text-slate-600 tw:relative tw:-top-[2px] tw:-ml-1 tw:mr-2 tw:cursor-pointer"
        @mousedown.stop.prevent
        @click="$emit('magnifying-glass-click', option)"
      />
      <!-- Copy default tag slot content from Vue-Multiselect -->
      {{ selectRef?.currentOptionLabel }}
    </template>

    <template #clear>
      <font-awesome-icon
        v-if="modelValue && !$attrs.multiple && $attrs.allowEmpty !== false && !$attrs.disabled"
        class="multiselect__clear"
        icon="xmark"
        @mousedown.prevent="clearValue"
      />
    </template>

    <!-- magnifying glass implementation when multiple=true using chip tag -->
    <template #tag="slotProps">
      <span class="multiselect__tag" @mousedown.prevent>
        <font-awesome-icon
          v-if="showMagnifyingGlass"
          icon="magnifying-glass"
          class="tw:text-2xs tw:text-slate-600 tw:hover:text-slate-50 tw:relative tw:-top-[2px] tw:-ml-1 tw:mr-2 tw:cursor-pointer"
          @click="$emit('magnifying-glass-click', slotProps.option)"
        />
        <!-- Copy default tag slot content from Vue-Multiselect -->
        <span v-text="selectRef?.getOptionLabel?.(slotProps.option)"></span>
        <i tabindex="1" @keydown.enter.prevent="slotProps.remove(slotProps.option)"
            @mousedown.prevent="slotProps.remove(slotProps.option)" class="multiselect__tag-icon"></i>
      </span>
    </template>
  </multiselect>
</template>
<script>
import Multiselect from 'vue-multiselect'
import { forceArray, hasValue } from '@/utils/misc'
import _ from 'lodash'
import { ref, shallowRef } from 'vue'
import { useBootstrapVueModal } from '@/composables/useBootstrapVueModal'
// See discussion on this issue:
// https://github.com/shentao/vue-multiselect/issues/385
export default {
  name: 'KeyMultiselect',
  inheritAttrs: false,
  setup () {
    const selectRef = shallowRef(null)
    return {
      selectRef
    }
  },
  components: {
    Multiselect
  },
  props: {
    // Originally, Boolean was the first allowed type in the following array.
    // But something strange happened: One taggable instances, an empty string value was
    // defaulting to boolean true. We saw this on EmployeeForm RelationshipSelector
    // component. Moving Boolean type to end resolves the issue.
    modelValue: [Number, String, Array, Boolean],
    options: Array,
    trackBy: String,
    taggable: {
      type: Boolean,
      default: false
    },
    resourceName: String,
    showAddNewItem: Boolean,
    showMagnifyingGlass: Boolean,
    showSelectAllOrNone: {
      type: Boolean,
      default: false
    },
    showLoadAndSelectAll: {
      type: Boolean,
      default: false
    },
    fetchAllLimit: {
      type: Number,
      default: undefined
    },
    size: {
      type: String,
      default: 'normal',
      validator: value => ['compact', 'normal'].includes(value)
    },
    // If autoPruneValues is true, then we automatically remove values from modelValue that
    // are not in the options.
    autoPruneValues: {
      type: Boolean,
      default: true
    },
    modalComponent: {
      type: [Object, Function],
      default: null
    },
  },
  emits: ['fullValueChange', 'update:modelValue', 'blur', 'focus', 'item-added', 'magnifying-glass-click', 'fetch-all-select-all'],
  data () {
    return {
      optionsLoaded: false,
      showModal: useBootstrapVueModal().showModal,
    }
  },
  computed: {
    completeValue: {
      get () {
        if (!hasValue(this.modelValue)) return null
        if (this.$attrs['multiple']) {
          // TODO: handle value not found if taggable
          return forceArray(this.modelValue).map(value => this.findOption(value)).filter(value => value)
        } else {
          // TODO: Value not getting cleared if not found in options.
          const completeValue = this.findOption(this.modelValue)
          if (completeValue === undefined && this.taggable) {
            this.addTag(this.modelValue)
          }
          return completeValue
        }
      },
      set (v) {
        this.$emit('update:modelValue', this.$attrs['multiple']
          ? v.map(value => value[this.trackBy])
          : (v && v[this.trackBy])
        )
      }
    },
    showSelectAll () {
      return Boolean(this.showSelectAllOrNone && this.$attrs['multiple'] && this.options.length > 1 && this.modelValue.length < this.options.length)
    },
    showSelectNone () {
      return Boolean(this.showSelectAllOrNone && this.$attrs['multiple'] && this.options.length > 1 && this.modelValue.length > 0)
    },
    computedShowLoadAndSelectAll () {
      return Boolean(this.showLoadAndSelectAll && this.$attrs['multiple'] && this.options.length > 1)
    },
    passThruSlots () {
      return Object.fromEntries(Object.entries(this.$slots)
        .filter(([k, _v]) => !['beforeList', 'afterList', 'clear', 'singleLabel'].includes(k))
      )
    }
  },
  watch: {
    modelValue: {
      handler () {
        if (this.autoPruneValues) {
          this.pruneValues()
        }
      }
    },
    completeValue: {
      handler (value, oldValue) {
        const newList = forceArray(value)
        const oldList = forceArray(oldValue)

        const newTrackBy = newList.map(v => _.get(v, this.trackBy))
        const oldTrackBy = oldList.map(v => _.get(v, this.trackBy))

        if (!_.isEqual(_.sortBy(newTrackBy), _.sortBy(oldTrackBy))) {
          this.$emit('fullValueChange', value)
        }
      },
      // Use immediate watcher so that we emit initial full value,
      // in case parent needs to display values,
      // e.g., print report filters as subtitles.
      immediate: true
    },
    options: {
      handler (value) {
        if (!_.isEmpty(value) && !this.optionsLoaded) {
          this.optionsLoaded = true
        }
        // TODO: Auto pruning doesn't work well when options come from
        // TODO: a computed field that gets updated multiple times as
        // TODO: different things load, e.g., report group by options.
        // TODO: For now, we'll turn off autoPruneValues specifically
        // TODO: for report group by and pivot selections. But if we
        // TODO: see additional regressions, we may need to remove
        // TODO: auto pruning more broadly.
        if (this.optionsLoaded && this.autoPruneValues) {
          this.pruneValues()
        }
      },
      immediate: true
    }
  },
  methods: {
    pruneValues () {
      if (!hasValue(this.modelValue)) return
      const values = forceArray(this.modelValue)
      const prunedValues = values.filter(value => !!this.findOption(value))
      if (!_.isEqual(values, prunedValues)) {
        this.$emit('update:modelValue', this.$attrs['multiple'] ? prunedValues : (prunedValues?.[0] ?? null))
        // TODO: Will and should the completeValue watcher be triggered?
      }
    },
    onChange (value) {
      this.completeValue = value
    },
    findOption (value) {
      return this.options.find(option => option[this.trackBy] === value)
    },
    addTag (value) {
      const newOption = {
        [this.trackBy]: value,
        [this.$attrs.label]: value
      }
      this.options.push(newOption)
      // TODO: if multiple then push
      this.completeValue = newOption
    },
    clearValue () {
      this.onChange(null)
    },
    loadAndSelectAll () {
      this.$emit('fetch-all-select-all')
    },
    selectAll () {
      this.onChange(this.options)
    },
    selectNone () {
      this.onChange([])
    },
    async addNewItem () {
      const itemId = ref(null)
      this.showModal({
        component: typeof this.modalComponent === 'function' ? await this.modalComponent() : this.modalComponent,
        props: {
          itemId: itemId,
          onSaved: item => {
            itemId.value = item.id
            this.$emit('update:modelValue', item.id)
            this.$emit('item-added', item)
          }
        }
      })
    }
  },
  mounted () {
    this.selectRef = this.$refs.select
    const selectEl = this.selectRef.$el
    if (selectEl) {
      const inputEl = selectEl.querySelector('input')
      if (inputEl) {
        inputEl.addEventListener('focus', event => this.$emit('focus', event))
        inputEl.addEventListener('blur', event => this.$emit('blur', event))
      }
    }
  }
}
</script>

<style>
@import 'vue-multiselect/dist/vue-multiselect.css';
</style>

<style>
.key-multiselect {
  &.multiselect--active {
    /* Use css variable to make it easier to override. */
    --multiselect-active-z-index: 50;
    z-index: var(--multiselect-active-z-index);
  }
  .multiselect__clear{
    display: none;
  }
  &.multiselect--clearable > .multiselect__clear{
    display: block;
    position: absolute;
    right: 40px;
    top: 13px;
    height: 14px;
    width: 14px;
    /* display: block; */
    cursor: pointer;
    z-index: 3;
    border: none;
    background: #fff;
    color: #999;

    &::before, &::after {
      content: "";
      background: #999999;
      top: 2px;
      left: 10px;
      display: block;
      cursor: pointer;
      z-index: 3;
      position: absolute;
      width: 3px;
      height: 16px;
      right: 4px;
    }
    &::before {
      transform: rotate(45deg)
    }
    &::after {
      transform: rotate(-45deg)
    }
  }

  &.multiselect-compact {
    
      min-width: 8rem;
      max-width: 10rem;
      min-height: inherit;

      .multiselect__single, .multiselect__input, .multiselect__option {
        font-size: 13px;
      }
      .multiselect__single {
        margin-bottom: 3px;
      }

      .multiselect__tags {
        padding: 3px 40px 0 3px;
        min-height: 30px;
        font-size: 12px;

        .multiselect__input {
          margin-bottom: 0;
        }
        .multiselect__spinner {
          width: 30px;
          height: 28px;
          &::before, &::after {
            width: 13px;
            height: 13px;
          }
        }
        .multiselect__placeholder {
          margin-bottom: 0;
          padding-left: 3px;
        }
        .multiselect__tag {
          margin-bottom: 2px;
          &:first-child {
            margin-top: 2px;
          }
        }
      }
      .multiselect__element {
        height: 30px;

        .multiselect__option {
          padding: 6px;
          min-height: 30px;
          &::after {
            line-height: 30px;
            font-size: 11px;
          }
        }
      }
      .multiselect__clear {
        top: 9px;
        right: 30px;
        height: 12px;
        width: 12px;
      }
      .multiselect__select {
        width: 30px;
        height: 28px;
        &::before {
          top: 75%;
        }
      }
    
  }


  .multiselect__option, .multiselect__single {
    max-width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  /* Default placeholder styling takes more height than single selected value.
    Let's make them the same height and padding. */
  .multiselect__placeholder {
    line-height: 20px;
    padding-top: 0px;
    margin-bottom: 8px;
  }

  .select-all-or-none {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    padding: 5px 12px;
    /* prevent remote multiselect li line-height override for this particular element */
    line-height: 1.5 !important;
    column-gap: 15px;
    a {
      margin-right: 1.5rem;
      font-size: 14px;
    }
  }

  .after-list-divider {
    position: sticky;
    bottom: 40px;
    background-color: white;
    margin-bottom: 0;
  }

  .add-new-item {
    position: sticky;
    bottom: 0;
    background-color: white;

    &:hover {
      background-color: var(--fc-logo-secondary-blue);
      color: #fff;
    }
  }
}
</style>
